import { ref, type Ref } from 'vue';
import type { Router } from 'vue-router';
import { Size } from '@/ptfm/themes/models/enums';
import { SettingUrlType } from '@/ptfm/apps/models/enums';
import type { INavigationHelper, ModalEntry, FormNavParams, PopupNavParams, AppNavParams, DrillNavParams, IClickAction } from '../INavigationHelper';

/**
 * NavigationHelper for the default theme.
 *
 * Provides Vue router-based navigation for forms, popups, details, and lists.
 * Each theme can have its own NavigationHelper implementing INavigationHelper.
 *
 * Ported from jQuery: tenants/themes/compact/js/navigation/navigationhelper.ts
 */
export class NavigationHelper implements INavigationHelper {

  private router: Router | null = null;
  private _modalStack: Ref<ModalEntry[]> = ref([]);
  private _loading: Ref<boolean> = ref(false);

  // ─── Reactive state exposed for components ───
  get modalStack() { return this._modalStack; }
  get loading() { return this._loading; }
  get hasOpenModals() { return this._modalStack.value.length > 0; }

  // ─── Initialization ───

  /**
   * Initialize with Vue Router instance. Call this once from the app setup.
   */
  init(router: Router) {
    this.router = router;
  }

  private getRouter(): Router {
    if (!this.router) {
      throw new Error('NavigationHelper: Router not initialized. Call init(router) first.');
    }
    return this.router;
  }

  // ─── Loading State ───

  showLoading() {
    this._loading.value = true;
  }

  hideLoading() {
    this._loading.value = false;
  }

  // ─── Form Navigation (ported from jQuery loadForm) ───

  /**
   * Open a form as an overlay (no route change).
   * The underlying page stays untouched — no reload on close.
   *
   * Two flavours, discriminated by params:
   *   - DB-id form: pass formId / pkId / configId → legacy FormView
   *   - File-backed form: pass appPath → form_renderer under /_apps/**
   */
  loadForm(params: FormNavParams) {
    const isFilePath = !!params.appPath;
    const size = params.size ?? (isFilePath ? Size.Popin : Size.Auto);
    const idKey = isFilePath ? params.appPath : params.formId;

    this.pushModal({
      id: `form_${idKey}_${Date.now()}`,
      type: 'form',
      size,
      formId: params.formId,
      configId: params.configId,
      pkId: params.pkId,
      appPath: params.appPath,
      appParams: params.appParams,
      onClose: params.callback,
    });
  }

  // ─── Popup Navigation (ported from jQuery popup) ───

  /**
   * Open a popup overlay (no route change).
   * jQuery equivalent: NavigationHelper.popup(id, containerid, component, callback, size)
   */
  openPopup(params: PopupNavParams) {
    const size = params.size ?? Size.Large;

    this.pushModal({
      id: `popup_${Date.now()}`,
      type: 'popup',
      size,
      route: params.route,
      onClose: params.callback ? () => params.callback!('') : undefined,
    });
  }

  // ─── App Navigation (load app page as overlay, no route change) ───

  /**
   * Open an app page as an overlay. Renders AppRenderer inside the modal stack.
   * The underlying page stays intact. Size defaults to Popin (6).
   */
  loadApp(params: AppNavParams) {
    const size = params.size ?? Size.Popin;

    this.pushModal({
      id: `app_${params.path}_${Date.now()}`,
      type: 'app',
      size,
      appPath: params.path,
      appParams: params.params || {},
      onClose: params.callback,
    });
  }

  // ─── Drill-down Navigation (report page as overlay) ───

  /**
   * Open a drill-down report page as an overlay. Delegates to loadApp —
   * the drill page is just a regular file-backed app page.
   *
   * Filter keys are *flattened* into routeParams so that query-widget's
   * `{{field}}` placeholder resolution (in settings.defaultFilters) picks
   * them up directly: pass `{ segment: 'Power' }` → the drill page's uqry
   * resolves `{{segment}}` → runtime filter `{ segment: 'Power' }`.
   *
   * `title` is a reserved key owned by the modal chrome.
   */
  openDrill(params: DrillNavParams) {
    this.loadApp({
      path: params.reportPath,
      params: {
        ...(params.defaultFilters || {}),
        title: params.title,
      },
      size: params.size ?? Size.Large,
      callback: params.callback,
    });
  }

  // ─── Detail Popup (ported from jQuery loadDetailPopup) ───

  /**
   * Open a detail page as an overlay (no route change).
   * jQuery equivalent: NavigationHelper.loadDetailPopup(id, installedappid, callback)
   */
  loadDetailPopup(recordId: number, installedAppId: number, callback?: () => void) {
    this.pushModal({
      id: `detail_${recordId}_${Date.now()}`,
      type: 'detail',
      size: Size.Large,
      // Detail popups identify a record by its primary key; reuse pkId so
      // ModalEntry stays unified with the form chain.
      pkId: recordId,
      installedAppId,
      onClose: callback,
    });
  }

  // ─── List Navigation (ported from jQuery loadList) ───

  /**
   * Navigate to an app's list page.
   * jQuery equivalent: NavigationHelper.loadList(installedappid, callback)
   */
  loadList(installedAppId: number) {
    const router = this.getRouter();
    router.push({
      path: `/apps/${installedAppId}/list`,
    });
  }

  // ─── Close Methods (ported from jQuery close/closePopUp/closeAllModals) ───

  /**
   * Close the topmost modal/popup overlay.
   * Does not affect the router — the underlying page stays intact.
   */
  closePopup() {
    const entry = this._modalStack.value.pop();
    if (entry?.onClose) {
      entry.onClose();
    }
  }

  /**
   * Close all open modal/popup overlays.
   * Does not affect the router — the underlying page stays intact.
   */
  closeAllModals() {
    for (const entry of this._modalStack.value) {
      if (entry.onClose) entry.onClose();
    }
    this._modalStack.value = [];
  }

  /**
   * Close the current detail view.
   * jQuery equivalent: NavigationHelper.close() / closeDetailsPage()
   */
  close() {
    if (this.hasOpenModals) {
      this.closePopup();
    }
  }

  // ─── Config Action Handler (ported from jQuery load_appconfigurations) ───

  /**
   * Handle a configuration click based on its settingUrlType.
   * jQuery equivalent: _ReportViewer.load_appconfigurations(config, templatetext, portletwidgetid)
   *
   * Returns true if the action was handled, false if the caller should handle it.
   */
  handleConfigAction(action: IClickAction): boolean {
    const urlType = action.settingUrlType;

    switch (urlType) {
      case SettingUrlType.Form:
        // settingUrl is the file-backed form path (e.g. "hrms/employees/create")
        // when present; otherwise fall back to the legacy DB-id form.
        if (action.settingUrl) {
          this.loadForm({
            appPath: action.settingUrl,
            appParams: action.params,
            size: action.size ?? this.mapFormSize(action.formSize),
          });
        } else {
          this.loadForm({
            formId: action.formId || 0,
            pkId: action.pkId || 0,
            configId: action.configId || 0,
            size: action.size ?? this.mapFormSize(action.formSize),
          });
        }
        return true;

      case SettingUrlType.Html:
        // HTML type: load JS file and open in popup
        // For Vue, we emit this back for legacy bridge handling
        return false;

      case SettingUrlType.JsFunction:
        // JS function type: execute the jsFunction string
        if (action.jsFunction) {
          try {
            const fn = new Function(action.jsFunction);
            fn();
          } catch (e) {
            console.error('NavigationHelper: Failed to execute jsFunction', action.jsFunction, e);
          }
          return true;
        }
        return false;

      case SettingUrlType.Delete:
        // Delete type: caller should handle with confirmation dialog
        return false;

      case SettingUrlType.Component:
        // Component type: navigate to the component route
        if (action.settingUrl) {
          this.getRouter().push(action.settingUrl);
          return true;
        }
        return false;

      default:
        return false;
    }
  }

  // ─── Size Helpers ───

  /**
   * Map a formSize number from config to a Size enum value.
   */
  /**
   * Map a formSize number from config to a Size enum value.
   * Maps formSize from API config to the Size enum.
   */
  mapFormSize(formSize?: number): Size {
    switch (formSize) {
      case 1: return Size.Mini;
      case 2: return Size.Medium;
      case 3: return Size.Large;
      case 4: return Size.ExtraLarge;
      case 5: return Size.Auto;
      case 6: return Size.Popin;
      default: return Size.Popin;
    }
  }

  /**
   * Get CSS class for a given Size enum.
   */
  getSizeClass(size: Size): string {
    switch (size) {
      case Size.Mini: return 'w-1/3 max-w-sm';
      case Size.Medium: return 'w-1/2 max-w-2xl';
      case Size.Large: return 'w-2/3 max-w-4xl';
      case Size.ExtraLarge: return 'w-full max-w-7xl';
      case Size.Popin: return 'h-full w-full';
      default: return 'w-2/3 max-w-4xl';
    }
  }

  /**
   * Check if a Size value should render as a centered modal overlay with backdrop.
   */
  isModalSize(size: Size): boolean {
    return size === Size.Mini || size === Size.Medium ||
           size === Size.Large || size === Size.ExtraLarge;
  }

  /**
   * Check if a Size value should render as a docked panel.
   */
  isDockedSize(_size: Size): boolean {
    return false;
  }

  /**
   * Check if a Size value should render as a popin — visually replaces the main
   * content area (looks like a page navigation) but is actually a modal overlay.
   * Closing it reveals the original page underneath, fully intact.
   */
  isPopinSize(size: Size): boolean {
    return size === Size.Popin;
  }

  // ─── Direction (RTL/LTR) ───

  /**
   * Get the current text direction from the app-root element's dir attribute.
   * Falls back to document.documentElement dir, then 'ltr'.
   */
  getDirection(): 'ltr' | 'rtl' {
    const appRoot = document.getElementById('app-root') || document.getElementById('app');
    const dir = appRoot?.getAttribute('dir')
      || document.documentElement.getAttribute('dir')
      || 'ltr';
    return dir === 'rtl' ? 'rtl' : 'ltr';
  }

  /**
   * Check if the current layout direction is RTL.
   */
  isRtl(): boolean {
    return this.getDirection() === 'rtl';
  }

  // ─── Modal Stack Management ───

  private pushModal(entry: ModalEntry) {
    this._modalStack.value.push(entry);
  }

  getLastModalId(): string | undefined {
    const last = this._modalStack.value[this._modalStack.value.length - 1];
    return last ? last.id : undefined;
  }

  // ─── Singleton ───

  private static _instance: NavigationHelper;
  static getInstance(): NavigationHelper {
    if (!NavigationHelper._instance) {
      NavigationHelper._instance = new NavigationHelper();
    }
    return NavigationHelper._instance;
  }
}
